import { BooleanRule } from '@/classes/rules/BooleanRule';
import { DefaultValue } from '@/classes/rules/DefaultValue';
import { EnumRule } from '@/classes/rules/EnumRule';
import { MinMaxRule } from '@/classes/rules/MinMaxRule';
import { ObjectRule } from '@/classes/rules/ObjectRule';
import { TextRule } from '@/classes/rules/TextRule';
import { EcoBreakdownExt } from '@/classes/scenario/eco/EcoBreakdownExt';
import { EcoFutureExt } from '@/classes/scenario/eco/EcoFutureExt';
import { EcoOMExt } from '@/classes/scenario/eco/EcoOMExt';
import { ScenarioEcoExt } from '@/classes/scenario/eco/ScenarioEcoExt';
import { ScenarioExt } from '@/classes/scenario/ScenarioExt';
import { CurrencyEnum, InjectionModelRef } from '@/dto/scenario/ScenarioReference';
import {
  DepreciationTypeRef,
  FeedInPricingTypeRef,
  GridPricingTypeRef,
  ProjectFinancingStructureTypeRef,
  RevenuesTypeRef
} from '@/dto/scenario/eco/EcoReference';
import { GridComplexPricingExt, PeriodValidation } from '@/classes/scenario/grid/GridComplexPricingExt';
import { ArrayRule } from '@/classes/rules/ArrayRule';
import { RuleFieldMessage } from '@/classes/rules/RuleFieldMessage';
import { GridPricingFileExt } from '@/classes/scenario/eco/GridPricingFileExt';
import { range, removeEndingNulls } from '@/helpers/arrayHelper';
import { EcoRevenuesExt } from '@/classes/scenario/eco/EcoRevenuesExt';
import { FeedInExt } from '@/classes/scenario/eco/FeedInExt';
import { ProjectTypeRef } from '@/dto/scenario/ProjectReference';

const hasGrid = (scenario: ScenarioExt) => !!scenario.hasGrid && !scenario.hasInjectionType(InjectionModelRef.total_injection);
const useAffiliateDebt = (scenario: ScenarioExt) =>
  !!(scenario.eco && scenario.eco.useAffiliateDebt);

// ECO RULES =========================================================
export const buildEcoRules = () => new ObjectRule<ScenarioEcoExt>('eco', [
  // CAPEX YEAR ONE
  new BooleanRule('hasBreakdownCapex'),

  new MinMaxRule('epcProposalFixed', 0, 1000000, '$')
    .withIsAvailable(scenario => (scenario.eco && scenario.eco.hasBreakdownCapex === false) || false),

  new MinMaxRule('epcProposalVariable', 0, 100, '$/wp')
    .withIsAvailable(scenario => (scenario.eco && scenario.eco.hasBreakdownCapex === false) || false),

  new ObjectRule<EcoBreakdownExt>('breakdown', buildBreakdownRules())
    .withDefaultValue(() => new DefaultValue(new EcoBreakdownExt()))
    .withIsAvailable(scenario => (scenario.eco && scenario.eco.hasBreakdownCapex) || false),

  new ObjectRule<EcoFutureExt>('future', buildFutureRules())
    .withIsAvailable(scenario => (scenario.eco && scenario.eco.hasBreakdownCapex === false) || false)
    .withDefaultValue(() => new DefaultValue(new EcoFutureExt())),

  new ObjectRule<EcoOMExt>('om', buildOmRules())
    .withDefaultValue(() => new DefaultValue(new EcoOMExt())),

  // GRID AND FUEL COST ESCALATION RATE
  new EnumRule('gridPricingTypeRef', GridPricingTypeRef)
    .withIsAvailable(hasGrid),

  new ObjectRule<GridComplexPricingExt>('gridComplexPricing', buildComplexPricingRules())
    .withIsAvailable(scenario => (scenario.hasGrid && scenario.eco && scenario.eco.gridPricingTypeRef === GridPricingTypeRef.complex_pricing) || false)
    .withCustomCheck((val, errorMessages) => {
      val.validatePeriods()
        .map((validationMessage, index) => {
          if (validationMessage === PeriodValidation.valid) {
            return null;
          }
          return new RuleFieldMessage(
            'scenario.eco.complexPricing.period.missingPrice',
            [`eco.gridComplexPricing.periods.${index}`]
          );
        })
        .filter((message): message is RuleFieldMessage => message !== null)
        .forEach(message => errorMessages?.push(message));
      return errorMessages?.length === 0;
    }),

  new MinMaxRule('gridFixFee', 0, 10000, '%/month')
    .withIsAvailable(hasGrid),

  new MinMaxRule('gridPrice', 0, 10, '$/kwh')
    .withIsAvailable((scenario) => hasGrid(scenario) && scenario.eco?.gridPricingTypeRef === GridPricingTypeRef.simple_pricing),

  new MinMaxRule('gridFixFeeEscalationRate', -1, 1, '%/year')
    .withIsAvailable(hasGrid)
    .withFactor(100),

  new MinMaxRule('gridPriceEscalationRate', -1, 1, '%/year')
    .withIsAvailable(hasGrid)
    .withFactor(100),

  new MinMaxRule('gridFixFeeTax', 0.5, 1, '%')
    .withIsAvailable(hasGrid)
    .withFactor(100),

  new MinMaxRule('gridPriceTax', 0.5, 1, '%')
    .withIsAvailable(hasGrid)
    .withFactor(100),

  new ObjectRule<GridPricingFileExt>('gridMyFilePricing', buildGridMyFilePricingRules())
    .withIsAvailable(scenario => (scenario && scenario.eco && scenario.eco.gridPricingTypeRef === GridPricingTypeRef.my_pricing) || false)
    .withCustomCheck(val => {
      if (!val.originalContent) {
        return false;
      }
      const lengthIsOk = 8760;
      const originalContent = val.originalContent;
      // check original content
      const splitReg = /\n/; // used to match a breakline
      const lines = originalContent.split(splitReg).filter((s: any) => s.length > 0);
      if (lines.length === lengthIsOk) {
        return true;
      }
      return false;
    }),

  // Generator
  new MinMaxRule('fuelPrice', 0, 10, '%/l')
    .withIsAvailable(scenario => (!!scenario.hasGenset && scenario.generator && !scenario.generator.isMulti) || false),

  new MinMaxRule('fuelEscalationRate', -1, 1, '%/year')
    .withIsAvailable(scenario => (!!scenario.hasGenset && scenario.generator && !scenario.generator.isMulti) || false)
    .withFactor(100),

  new MinMaxRule('fuelPriceType1', 0, 10, '%/l')
    .withIsAvailable(scenario => (!!scenario.hasGenset && scenario.generator && scenario.generator.isMulti) || false),

  new MinMaxRule('fuelEscalationRateType1', -1, 1, '%/year')
    .withIsAvailable(scenario => (!!scenario.hasGenset && scenario.generator && scenario.generator.isMulti) || false)
    .withFactor(100),

  new MinMaxRule('fuelPriceType2', 0, 10, '$/kWh')
    .withIsAvailable(scenario => (!!scenario.hasGenset && scenario.generator && scenario.generator.isMulti && scenario.generator.hasDifferentTypes) || false),

  new MinMaxRule('fuelEscalationRateType2', -1, 1, '%/year')
    .withIsAvailable(scenario => (!!scenario.hasGenset && scenario.generator && scenario.generator.isMulti && scenario.generator.hasDifferentTypes) || false)
    .withFactor(100),

  new ObjectRule('feedIn', [
    new EnumRule('feedInPricingTypeRef', FeedInPricingTypeRef)
      .withIsAvailable(scenario => scenario.hasInjectionType(InjectionModelRef.total_injection) &&
        (scenario.project?.typeRef !== ProjectTypeRef.utility_scale_pv_plant_project && scenario.project?.typeRef !== ProjectTypeRef.wind_onshore_project)),

    new MinMaxRule('feedInDuration', 1)
      .withIsAvailable((scenario) => scenario.hasInjectionType(InjectionModelRef.total_injection) &&
        scenario.eco?.feedIn.feedInPricingTypeRef === FeedInPricingTypeRef.simple_pricing),

    new MinMaxRule('feedInTariff', 0, 1000, '$/kWh')
      .withIsAvailable((scenario: ScenarioExt) => (
        (scenario.hasInjectionType(InjectionModelRef.self_consumption_with_injection) || scenario.hasInjectionType(InjectionModelRef.total_injection)) &&
        scenario.eco?.feedIn.feedInPricingTypeRef === FeedInPricingTypeRef.simple_pricing) ?? false),

    new MinMaxRule('feedInTariff2', 0)
      .withIsAvailable(scenario => (scenario.hasInjectionType(InjectionModelRef.total_injection) &&
        (scenario.project?.typeRef !== ProjectTypeRef.utility_scale_pv_plant_project && scenario.project?.typeRef !== ProjectTypeRef.wind_onshore_project) &&
        scenario.eco?.feedIn.feedInPricingTypeRef === FeedInPricingTypeRef.simple_pricing &&
        (scenario.eco?.feedIn.feedInDuration ?? scenario.durationYear) !== scenario.durationYear) ?? false),

    new MinMaxRule('feedInTariffEscalationRate', -1, 1, '%/year')
      .withIsAvailable((scenario: ScenarioExt) => (
        (scenario.hasInjectionType(InjectionModelRef.self_consumption_with_injection) || scenario.hasInjectionType(InjectionModelRef.total_injection)) &&
        scenario.eco?.feedIn.feedInPricingTypeRef === FeedInPricingTypeRef.simple_pricing) ?? false)
      .withFactor(100),

    new MinMaxRule('feedInTariffEscalationRate2', 0)
      .withIsAvailable(scenario => (scenario.hasInjectionType(InjectionModelRef.total_injection) &&
        (scenario.project?.typeRef !== ProjectTypeRef.utility_scale_pv_plant_project && scenario.project?.typeRef !== ProjectTypeRef.wind_onshore_project) &&
        scenario.eco?.feedIn.feedInPricingTypeRef === FeedInPricingTypeRef.simple_pricing &&
        (scenario.eco?.feedIn.feedInDuration ?? scenario.durationYear) !== scenario.durationYear) ?? false),

    new MinMaxRule('feedInPPAvsMerchant', 0, 1, '%')
      .withIsAvailable(scenario => scenario.hasInjectionType(InjectionModelRef.total_injection) &&
        (scenario.project?.typeRef === ProjectTypeRef.utility_scale_pv_plant_project || scenario.project?.typeRef === ProjectTypeRef.wind_onshore_project))
      .withDefaultValue(() => new DefaultValue(1)),

    new BooleanRule('feedInMerchantPriceVector')
      .withIsAvailable((scenario) => {
        const ppaDuration = scenario.eco?.feedIn.feedInDuration ?? 9;
        const projectDuration = scenario.durationYear ?? 9;
        const ppaVsMerchant = scenario.eco?.feedIn.feedInPPAvsMerchant ?? 0;
        return (scenario.project?.typeRef === ProjectTypeRef.utility_scale_pv_plant_project || scenario.project?.typeRef === ProjectTypeRef.wind_onshore_project) &&
        (ppaDuration < projectDuration || ppaVsMerchant < 1);
      }),

    new MinMaxRule('routeToMarket', 0, 1, '%')
      .withIsAvailable((scenario) => {
        const ppaDuration = scenario.eco?.feedIn.feedInDuration ?? 9;
        const projectDuration = scenario.durationYear ?? 9;
        const ppaVsMerchant = scenario.eco?.feedIn.feedInPPAvsMerchant ?? 0;
        return (scenario.project?.typeRef === ProjectTypeRef.utility_scale_pv_plant_project || scenario.project?.typeRef === ProjectTypeRef.wind_onshore_project) &&
        (ppaDuration < projectDuration || ppaVsMerchant < 1);
      }),

    new BooleanRule('hasPaymentForExcessAnnualGeneration')
      .withIsAvailable(scenario => scenario.hasInjectionType(InjectionModelRef.net_metering)),

    new ObjectRule<GridPricingFileExt>('feedInFlexibleTariff', [
      new TextRule('originalContent', undefined, undefined, (value, scenario) => {
        if (value === null) {
          return null;
        }
        const lines = value.split('\n');
        if (scenario.durationYear === null || lines.length !== scenario.durationYear + 1) {
          return false;
        }
        const hasInvalidLines = lines.find(line => line === '' || isNaN(line as unknown as number));
        return hasInvalidLines === undefined;
      })
    ])
      .withIsAvailable((scenario) => {
        // Value 9 is minimum possible value for the duration for one project
        const ppaDuration = scenario.eco?.feedIn.feedInDuration ?? 9;
        const projectDuration = scenario.durationYear ?? 9;
        const ppaVsMerchant = scenario.eco?.feedIn.feedInPPAvsMerchant ?? 0;
        return (scenario.hasInjectionType(InjectionModelRef.total_injection) &&
          scenario.eco?.feedIn.feedInPricingTypeRef === FeedInPricingTypeRef.flexible_pricing) ||
          ((scenario.project?.typeRef === ProjectTypeRef.utility_scale_pv_plant_project || scenario.project?.typeRef === ProjectTypeRef.wind_onshore_project) &&
          (ppaDuration < projectDuration || ppaVsMerchant < 1));
      })
  ])
    .withDefaultValue(() => new DefaultValue(new FeedInExt()))
    .withIsAvailable((scenario) => (scenario.hasGrid && scenario.grid?.hasGrid24 && scenario.hasInjection === true) ?? false),

  // Affiliate Debt
  new BooleanRule('useAffiliateDebt'),

  new BooleanRule('useFarmdown')
    .withIsAvailable((scenario) => (scenario.project?.typeRef === ProjectTypeRef.utility_scale_pv_plant_project || scenario.project?.typeRef === ProjectTypeRef.wind_onshore_project) && scenario.hasInjectionType(InjectionModelRef.total_injection) && useAffiliateDebt(scenario) && (scenario.totalEnergiesShareholdingInterest ?? 0) > 0.65),

  new MinMaxRule('affiliateGearing', 0, 1, '%')
    .withIsAvailable(useAffiliateDebt)
    .withFactor(100),

  new MinMaxRule('minimumDscr')
    .withIsAvailable(useAffiliateDebt),

  new MinMaxRule('tenor', 0, 100, 'years')
    .withIsAvailable(useAffiliateDebt),

  new MinMaxRule('shortTermInterestRate', 0, 1, '%')
    .withFactor(100)
    .withIsAvailable(useAffiliateDebt),

  // IMPORT TAXES
  new BooleanRule('import_taxes')
    .withIsAvailable((scenario) => (scenario.project?.typeRef !== ProjectTypeRef.utility_scale_pv_plant_project && scenario.project?.typeRef !== ProjectTypeRef.wind_onshore_project)),

  new MinMaxRule('gensetTaxes', 0, 1, '%')
    .withIsAvailable(scenario => !!scenario.hasGenset)
    .withFactor(100),

  new MinMaxRule('shelterTaxes', 0, 1, '%')
    .withFactor(100),

  new MinMaxRule('pvModuleTaxes', 0, 1, '%')
    .withFactor(100),

  new MinMaxRule('pvRackingTaxes', 0, 1, '%')
    .withFactor(100),

  new MinMaxRule('pvInverterTaxes', 0, 1, '%')
    .withFactor(100),

  new MinMaxRule('batteryTaxes', 0, 1, '%')
    .withIsAvailable(scenario => !!scenario.hasBattery)
    .withFactor(100),

  new MinMaxRule('installationTaxes', 0, 1, '%')
    .withFactor(100),

  // FIXED YEARLY CASH FLOW
  new BooleanRule('additionalCashFlow')
    .withIsAvailable((scenario) => (scenario.project?.typeRef === ProjectTypeRef.utility_scale_pv_plant_project || scenario.project?.typeRef === ProjectTypeRef.wind_onshore_project)),

  new MinMaxRule('fixedYearlyCashFlowOtherCost', -200000, 200000, '$/year')
    .withDefaultValue(() => new DefaultValue(0))
    .withIsAvailable(scenario => (
      (scenario.project?.typeRef === ProjectTypeRef.utility_scale_pv_plant_project || scenario.project?.typeRef === ProjectTypeRef.wind_onshore_project) &&
      scenario.eco?.additionalCashFlow === true) || ((scenario.project?.typeRef !== ProjectTypeRef.utility_scale_pv_plant_project && scenario.project?.typeRef !== ProjectTypeRef.wind_onshore_project))
    ),

  new MinMaxRule('fixedYearlyCashFlowOtherCostYearsAffected', 0, 25, 'years')
    .withDefaultValue(() => new DefaultValue(25))
    .withIsAvailable(scenario => (
      (scenario.project?.typeRef === ProjectTypeRef.utility_scale_pv_plant_project || scenario.project?.typeRef === ProjectTypeRef.wind_onshore_project) &&
      scenario.eco?.additionalCashFlow === true) || ((scenario.project?.typeRef !== ProjectTypeRef.utility_scale_pv_plant_project && scenario.project?.typeRef !== ProjectTypeRef.wind_onshore_project))
    ),

  new MinMaxRule('totalAmountOverConstruction', -1000000000, 1000000000, 'MOCmod')
    .withDefaultValue(() => new DefaultValue(0))
    .withIsAvailable(scenario => (
      scenario.eco?.additionalCashFlow === true)
    ),

  // REVENUES TYPE
  new ObjectRule<EcoRevenuesExt>('revenues', [
    new EnumRule('revenuesTypeRef', RevenuesTypeRef),

    new BooleanRule('ppaTakeOrPay')
      .withIsAvailable(scenario => scenario.eco?.revenues.revenuesTypeRef === RevenuesTypeRef.ppa),

    new MinMaxRule('minimumTakeOff')
      .withIsAvailable(scenario => (
        scenario.eco?.revenues.revenuesTypeRef === RevenuesTypeRef.ppa &&
        scenario.eco?.revenues.ppaTakeOrPay === true)
      ),

    new MinMaxRule('contractDuration', 1)
      .withIsAvailable(scenario => (
        scenario.eco?.revenues.revenuesTypeRef === RevenuesTypeRef.solar_monthly_fee ||
        scenario.eco?.revenues.revenuesTypeRef === RevenuesTypeRef.solar_monthly_fee_by_value ||
        scenario.eco?.revenues.revenuesTypeRef === RevenuesTypeRef.ppa)),

    new MinMaxRule('solarMonthlyFeePercentage', 0, 1, '%')
      .withIsAvailable(scenario => scenario.eco?.revenues.revenuesTypeRef === RevenuesTypeRef.solar_monthly_fee)
      .withFactor(100),

    new MinMaxRule('solarMonthlyFeeYearlyInflation', 0, 1, '%')
      .withIsAvailable(scenario => (
        scenario.eco?.revenues.revenuesTypeRef === RevenuesTypeRef.solar_monthly_fee ||
        scenario.eco?.revenues.revenuesTypeRef === RevenuesTypeRef.solar_monthly_fee_by_value))
      .withFactor(100),

    new MinMaxRule('solarMonthlyFeeValue', 0, 100000, '$/month')
      .withIsAvailable(scenario => scenario.eco?.revenues.revenuesTypeRef === RevenuesTypeRef.solar_monthly_fee_by_value),

    new MinMaxRule('proportionOfSavings', 0, 1, '%')
      .withIsAvailable(scenario => scenario.eco?.revenues.revenuesTypeRef === RevenuesTypeRef.savings)
      .withFactor(100)
      .withDefaultValue(() => new DefaultValue(1)),

    new MinMaxRule('ppaPrice', 0, 100, '%/kWh')
      .withIsAvailable(scenario => scenario.eco?.revenues.revenuesTypeRef === RevenuesTypeRef.ppa),

    new MinMaxRule('ppaYearlyInflation', -1, 1, '%')
      .withIsAvailable(scenario => scenario.eco?.revenues.revenuesTypeRef === RevenuesTypeRef.ppa)
      .withFactor(100)
  ])
    .withDefaultValue(() => new DefaultValue(new EcoRevenuesExt()))
    .withIsAvailable(scenario => !scenario.hasInjectionType(InjectionModelRef.total_injection)),

  // Others
  new MinMaxRule('corporateTaxRate', 0, 1, '%/year')
    .withFactor(100),

  new MinMaxRule('discountRate', 0, 1, '%/year')
    .withFactor(100),

  new EnumRule('currency', CurrencyEnum),

  new MinMaxRule('exchangeRate', 0, 1000000000, '$'),

  new MinMaxRule('inflationRateLocalCurrency', 0, 1, '%')
    .withFactor(100),

  new MinMaxRule('inflationRateUSD', 0, 1, '%')
    .withDefaultValue(() => new DefaultValue(0.02))
    .withFactor(100),

  // PROJECT FINANCING STRUCTURE
  new EnumRule('projectFinancingStructureTypeRef', ProjectFinancingStructureTypeRef),

  new MinMaxRule('lossCarryForwardLimit', 0, 100, 'years')
    .withIsAvailable(scenario => !!(scenario.eco && scenario.eco.projectFinancingStructureTypeRef === ProjectFinancingStructureTypeRef.spv_spe)),

  new MinMaxRule('withholdingTaxOnDividends')
    .withFactor(100)
    .withIsAvailable((scenario) => (scenario.project?.typeRef === ProjectTypeRef.utility_scale_pv_plant_project || scenario.project?.typeRef === ProjectTypeRef.wind_onshore_project)),

  new MinMaxRule('taxHoliday')
    .withIsAvailable((scenario) => (scenario.project?.typeRef === ProjectTypeRef.utility_scale_pv_plant_project || scenario.project?.typeRef === ProjectTypeRef.wind_onshore_project)),

  // DEPRECIATION
  new EnumRule('depreciationTypeRef', DepreciationTypeRef),

  new MinMaxRule('depreciationTime', 1, 100, 'years')
    .withIsAvailable(scenario => (scenario.eco?.depreciationTypeRef !== DepreciationTypeRef.progressive)),

  new MinMaxRule('depreciationPercentage')
    .withIsAvailable(scenario => (scenario.eco?.depreciationTypeRef === DepreciationTypeRef.progressive)),

  // O&M
  // SUBSIDIES AND TAX CREDITS
  new BooleanRule('hasTaxCredit'),

  new MinMaxRule('taxCreditRate', 0, 1, '%')
    .withIsAvailable(scenario => !!(scenario.eco && scenario.eco.hasTaxCredit))
    .withDefaultValue(() => new DefaultValue(0))
    .withFactor(100),

  new MinMaxRule('taxCreditYear', 0, 25, 'year')
    .withDefaultValue(() => new DefaultValue(0))
    .withIsAvailable(scenario => !!(scenario.eco && scenario.eco.hasTaxCredit))
])
  .withIsAvailable(scenario => !isRenFranceProject(scenario))
  .withDefaultValue(() => new DefaultValue(new ScenarioEcoExt()));

function buildBreakdownRules() {
  return [
    // PV
    new MinMaxRule('pvModule', 0, 10, '$/wp'),

    new MinMaxRule('pvRackSystem', 0, 10, '$/wp')
      .withDefaultValue(() => new DefaultValue(0.1)),

    new MinMaxRule('pvInverter', 0, 10, '$/wp')
      .withDefaultValue(() => new DefaultValue(0.08)),

    new MinMaxRule('pvInverterAcdcRatio', 0, 2, '%')
      .withDefaultValue(() => new DefaultValue(1.2))
      .withFactor(100),

    new MinMaxRule('pvLogisticAndTransport', 0, 10, '$/wp')
      .withDefaultValue(() => new DefaultValue(0.15)),

    // Other equipments
    new MinMaxRule('otherAntiInjectionDevice', 0, 1000, '$')
      .withDefaultValue(() => new DefaultValue(500))
      .withIsAvailable(
        scenario =>
          (scenario.eco && scenario.eco.hasBreakdownCapex && scenario.hasGrid && !scenario.hasInjection) ||
          false
      ),

    new MinMaxRule('otherAcBillOfService', 0, 10, '$/w')
      .withDefaultValue(() => new DefaultValue(0.06)),

    new MinMaxRule('otherDcBillOfService', 0, 10, '$/wp')
      .withDefaultValue(() => new DefaultValue(0.06)),

    new MinMaxRule('otherLogisticAndTransport', 0, 10, '$/wp')
      .withDefaultValue(() => new DefaultValue(0)),

    // Structure
    new MinMaxRule('pvStructureCivilWorks', 0, 10000000, '$')
      .withDefaultValue(() => new DefaultValue(7000)),

    new MinMaxRule('pvStructureShelter', 0, 10000000, '$')
      .withDefaultValue(() => new DefaultValue(3000)),

    // Installation
    new MinMaxRule('projectInstallationPriceFixed', 0, 10000, '$')
      .withDefaultValue(() => new DefaultValue(5000)),

    new MinMaxRule('pvMechanicalInstallation', 0, 10, '$/wp')
      .withDefaultValue(() => new DefaultValue(0.1)),

    new MinMaxRule('pvElectricalInstallation', 0, 10, '$/wp')
      .withDefaultValue(() => new DefaultValue(0.15)),

    // Project
    new MinMaxRule('projectAffiliatesAndHq', 0, 100, '$/wp')
      .withDefaultValue(() => new DefaultValue(0)),

    new MinMaxRule('projectEngineeringAndAdministrative', 0, 10000, '$/wp')
      .withDefaultValue(() => new DefaultValue(0)),

    new MinMaxRule('projectEpcMargin', 0, 1, '%')
      .withDefaultValue(() => new DefaultValue(0.15))
      .withFactor(100),

    new MinMaxRule('projectSupervision', 0, 500000, '$')
      .withDefaultValue(() => new DefaultValue(0)),

    // Generator
    // Generator system before
    new MinMaxRule('generatorBeforeDayCost', 0, 10, '$/va')
      .withIsAvailable(scenario => (scenario.hasGenset && scenario.generator && !scenario.generator.isMulti) || false),

    new MinMaxRule('generatorBeforeNightCost', 0, 10, '$/va')
      .withIsAvailable(scenario => (scenario.hasGenset &&
          scenario.generator &&
          !scenario.generator.isMulti &&
          scenario.generator.hasDifferentGeneratorDayAndNightBefore) ||
        false),

    new MinMaxRule('generatorBeforeLogisticAndTransport', 0, 10, '$/va')
      .withDefaultValue(() => new DefaultValue(0))
      .withIsAvailable(scenario => (scenario.hasGenset && scenario.generator &&
        !scenario.generator.isMulti) || false),

    // Generator system after
    new MinMaxRule('generatorAfterDayCost', 0, 10, '$/va')
      .withIsAvailable(scenario => (scenario.hasGenset &&
          scenario.generator &&
          !scenario.generator.isMulti &&
          scenario.generator.hasDifferentGeneratorAfter) ||
        false),

    new MinMaxRule('generatorAfterNightCost', 0, 10, '$/va')
      .withIsAvailable(scenario => (scenario.hasGenset &&
          scenario.generator &&
          scenario.generator.hasDifferentGeneratorAfter &&
          !scenario.generator.isMulti &&
          scenario.generator.hasDifferentGeneratorDayAndNightAfter) ||
        false),

    new MinMaxRule('generatorAfterLogisticAndTransport', 0, 10, '$/va')
      .withDefaultValue(() => new DefaultValue(0))
      .withIsAvailable(
        scenario =>
          (scenario.hasGenset &&
            scenario.generator &&
            !scenario.generator.isMulti &&
            scenario.generator.hasDifferentGeneratorAfter) ||
          false
      ),

    new MinMaxRule('generatorCostType1', 0, 10, '$/va')
      .withIsAvailable(scenario => (scenario.hasGenset &&
          scenario.generator &&
          scenario.generator.isMulti) ||
        false),

    new MinMaxRule('generatorCostType2', 0, 10, '$/va')
      .withIsAvailable(scenario => (scenario.hasGenset &&
          scenario.generator &&
          scenario.generator.isMulti &&
          scenario.generator.hasDifferentTypes) ||
        false),

    new MinMaxRule('generatorLogisticAndTransport', 0, 10, '$/va')
      .withDefaultValue(() => new DefaultValue(0))
      .withIsAvailable(
        scenario =>
          (scenario.hasGenset &&
            scenario.generator &&
            scenario.generator.isMulti) ||
          false
      ),

    // Battery
    // Battery system
    new MinMaxRule('batteryModule', 0, 1000, '$/kwh')
      .withIsAvailable(scenario => scenario.hasBattery || false),

    new MinMaxRule('batteryPriceReduction', 0, 1, '%/year')
      .withDefaultValue(() => new DefaultValue(0.02))
      .withIsAvailable(scenario => scenario.hasBattery || false)
      .withFactor(100),

    new MinMaxRule('batteryInverter', 0, 100, '$/w')
      .withDefaultValue(() => new DefaultValue(0.37))
      .withIsAvailable(scenario => scenario.hasBattery || false),

    new MinMaxRule('batteryLogisticAndTransport', 0, 100, '$/w')
      .withDefaultValue(() => new DefaultValue(0))
      .withIsAvailable(scenario => scenario.hasBattery || false),

    new MinMaxRule('batteryStructureCivilWorks', 0, 10000, '$')
      .withDefaultValue(() => new DefaultValue(3000))
      .withIsAvailable(scenario => scenario.hasBattery || false),

    new MinMaxRule('batteryStructureShelter', 0, 10000, '$')
      .withDefaultValue(() => new DefaultValue(0))
      .withIsAvailable(scenario => scenario.hasBattery || false),

    new MinMaxRule('batteryMechanicalInstallation', 0, 100, '$/kwh')
      .withDefaultValue(() => new DefaultValue(10))
      .withIsAvailable(scenario => scenario.hasBattery || false),

    new MinMaxRule('batteryElectricalInstallation', 0, 100, '$/kwh')
      .withDefaultValue(() => new DefaultValue(10))
      .withIsAvailable(scenario => scenario.hasBattery || false)
  ];
}

function buildFutureRules() {
  return [
    new MinMaxRule('pvInverter', 0, 10, '$/wp')
      .withDefaultValue(() => new DefaultValue(0.08)),

    new MinMaxRule('pvInverterAcdcRatio', 0, 2, '%')
      .withDefaultValue(() => new DefaultValue(1.2))
      .withFactor(100),

    new MinMaxRule('generatorBeforeDayCost', 0, 100, '$/va')
      .withIsAvailable(scenario => (scenario.generator && !scenario.generator.isMulti) || false),

    new MinMaxRule('generatorBeforeNightCost', 0, 100, '$/va')
      .withIsAvailable(scenario => (scenario.hasGenset &&
          scenario.generator &&
          !scenario.generator.isMulti &&
          scenario.generator.hasDifferentGeneratorDayAndNightBefore) ||
        false),

    new MinMaxRule('generatorAfterDayCost', 0, 100, '$/va')
      .withIsAvailable(scenario => (scenario.hasGenset &&
          scenario.generator &&
          !scenario.generator.isMulti &&
          scenario.generator.hasDifferentGeneratorAfter) ||
        false),

    new MinMaxRule('generatorAfterNightCost', 0, 100, '$/va')
      .withIsAvailable(scenario => (scenario.hasGenset &&
          scenario.generator &&
          !scenario.generator.isMulti &&
          scenario.generator.hasDifferentGeneratorAfter &&
          scenario.generator.hasDifferentGeneratorDayAndNightAfter) ||
        false),

    new MinMaxRule('generatorCostType1', 0, 100, '$/va')
      .withIsAvailable(scenario => (scenario.hasGenset &&
          scenario.generator &&
          scenario.generator.isMulti) ||
        false),

    new MinMaxRule('generatorCostType2', 0, 100, '$/va')
      .withIsAvailable(scenario => (scenario.hasGenset &&
          scenario.generator &&
          scenario.generator.isMulti &&
          scenario.generator.hasDifferentTypes) ||
        false),

    new MinMaxRule('batteryModule', 0, 1000, '$/kwh')
      .withIsAvailable(scenario => scenario.hasBattery || false),

    new MinMaxRule('batteryPriceReduction', 0, 1, '%/year')
      .withIsAvailable(scenario => scenario.hasBattery || false)
      .withDefaultValue(() => new DefaultValue(0.02))
      .withFactor(100),

    new MinMaxRule('batteryInverter', 0, 100, '$/wh')
      .withIsAvailable(scenario => scenario.hasBattery || false)
      .withDefaultValue(() => new DefaultValue(0.37))
  ];
}

function buildOmRules() {
  return [
    new MinMaxRule('pvCost', 0, 100, '$/year/kwp'),

    new MinMaxRule('pvFixedCost'),

    new MinMaxRule('batteryCost', 0, 10000000, '$/year')
      .withIsAvailable((scenario: ScenarioExt) => scenario.hasBattery || false),

    new MinMaxRule('generatorFixedCost', 0, 10000, '$/year')
      .withIsAvailable((scenario: ScenarioExt) => scenario.hasGenset || false),

    new MinMaxRule('generatorVariableCost', 0, 25, '$/year/kw')
      .withIsAvailable((scenario: ScenarioExt) => scenario.hasGenset || false)
  ];
}

function buildComplexPricingRules() {
  return [
    new ArrayRule('prices', () => true,
      new MinMaxRule('sample', 0, undefined)
        .withRequired(false)
    ).withCustomCheck((gridPrices: number[], errorMessages) => {
      const messages = errorMessages || [];
      const prices = removeEndingNulls(gridPrices);
      const message = noPriceDefined(gridPrices) || haveMissingPrices(prices);
      if (message) {
        messages.push(message);
      }
      return messages.length === 0;
    }),

    new ArrayRule('allowBatteryCharge', scenario => scenario.hasBattery === true,
      new BooleanRule('sample', () => true)
    ),

    new ArrayRule('periods', () => true, new ObjectRule('sample'))
  ];
}

function buildGridMyFilePricingRules() {
  return [
    new MinMaxRule('durationYear', 0),

    new TextRule('name', undefined),

    new TextRule('originalContent', undefined, undefined, val => {
      if (val === null) {
        return null;
      }
      const splitReg = /\n/; // used to match a breakline
      const numberReg = /^(\d+\.?\d*)$/; // match number.decimals
      const lines = val.split(splitReg);
      /**
       * We reverse-iterate because if there is an error, it's most likely in last lines the user has entered.
       * NOTE HERE : as i-- is directly in the evaluation, 'i' is evaluated as it is, then passed as 'i - 1'
       * Example : length of 1 ; i = 1 ; i evaluated as 1, so true ; i used as 1 - 1 = 0.
       * So on an array of length 1, it uses index 0, on an array of length 3, it uses indexes 2, 1, 0.
       */
      for (let i = lines.length; i--;) {
        if (lines[i] && !numberReg.test(lines[i])) {
          return false;
        }
      }
      return true;
    })
  ];
}

function noPriceDefined(prices: (number | null)[]): RuleFieldMessage | null {
  if (prices.length !== 0 && prices.every(price => price === null)) {
    return buildPriceError(0, prices.length);
  }
  return null;
}

function haveMissingPrices([price, ...rest]: (number | null)[], index: number = 0, pricesNumber: number = rest.length + 1): RuleFieldMessage | null {
  if (rest.length === 0) {
    return null;
  }
  if ((price === null && rest.find(price => price !== null))) {
    return buildPriceError(index, pricesNumber);
  }
  return haveMissingPrices(rest, index + 1, pricesNumber);
}

function buildPriceError(start: number, end: number): RuleFieldMessage {
  const errorMessage = { key: 'gridPriceError', params: { fieldNumber: start + 1 } };
  const fieldsInError = range(start, end)
    .map(fieldIndex => `scenario.eco.gridPrices.price.${fieldIndex}`);
  return new RuleFieldMessage(errorMessage, fieldsInError);
}

function isRenFranceProject(scenario: ScenarioExt): boolean {
  return (scenario.project?.typeRef === ProjectTypeRef.ren_fr_pv_project || scenario.project?.typeRef === ProjectTypeRef.ren_fr_wind_project) &&
    scenario.hasInjectionType(InjectionModelRef.total_injection);
}
